import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DailyVerse from "../components/DailyVerse";
import NotificationPrompt from "../components/NotificationPrompt";
import { youtubeId } from "./ReadingPlans";
import { BookOpen, Megaphone, ArrowRight, HandHeart, BookHeart, MessageCircle, Youtube, Play, X, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [videos, setVideos] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [devotionals, setDevotionals] = useState([]);
  const [daily, setDaily] = useState([]);
  const [dailyText, setDailyText] = useState("");
  const [postingDaily, setPostingDaily] = useState(false);

  const loadDaily = () => api.get("/daily-encouragement").then((r) => setDaily(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/study-videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/devotionals").then((r) => setDevotionals(r.data)).catch(() => {});
    loadDaily();
  }, []);

  const postDaily = async () => {
    if (!dailyText.trim()) return;
    setPostingDaily(true);
    try {
      await api.post("/daily-encouragement", { body: dailyText });
      setDailyText("");
      toast.success("Today's encouragement is live for 24 hours");
      loadDaily();
    } catch (e) { toast.error("Could not post encouragement"); }
    finally { setPostingDaily(false); }
  };

  const removeDaily = async (id) => {
    setDaily((d) => d.filter((x) => x.id !== id));
    try { await api.delete(`/daily-encouragement/${id}`); } catch { loadDaily(); }
  };

  const timeLeft = (expires) => {
    const ms = new Date(expires) - new Date();
    if (ms <= 0) return "expiring";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h >= 1 ? `${h}h left` : `${m}m left`;
  };

  const latestVideo = videos[0];
  const latestDevos = devotionals.slice(0, 3);
  const visibleAnnouncements = announcements.filter((a) => !a.dismissed);

  const dismissAnnouncement = async (id) => {
    setAnnouncements((a) => a.map((x) => (x.id === id ? { ...x, dismissed: true } : x)));
    try { await api.post(`/announcements/${id}/dismiss`); } catch { /* keep it hidden locally regardless */ }
  };

  return (
    <div className="space-y-10">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#8C6A54] font-semibold">The Sanctuary</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Grace and peace, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-[#615A50] mt-2">A quiet place to meet with God, His Word, and your family in Christ.</p>
      </div>

      <NotificationPrompt />

      <DailyVerse />

      {/* Daily encouragement (24h) */}
      <div className="space-y-4 fade-up" data-testid="daily-encouragement">
        {isAdmin && (
          <div className="rounded-2xl border border-[#C46B4E]/40 bg-[#C46B4E]/5 p-5" data-testid="daily-composer">
            <div className="flex items-center gap-2 text-[#C46B4E]">
              <Sparkles className="w-5 h-5" />
              <p className="font-medium text-[#2A2621]">Share today's encouragement</p>
            </div>
            <p className="text-sm text-[#615A50] mt-1">A short word of encouragement for your members. It stays for 24 hours, then gently disappears — post a fresh one anytime.</p>
            <textarea value={dailyText} onChange={(e) => setDailyText(e.target.value)} rows={3} placeholder="e.g. Be still and know that He is God. You are held today."
              data-testid="daily-input"
              className="w-full mt-3 rounded-xl border border-[#D5CEBF] bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#C46B4E]/40 resize-none" />
            <button onClick={postDaily} disabled={postingDaily || !dailyText.trim()} data-testid="daily-post-btn"
              className="mt-3 rounded-full bg-[#C46B4E] text-white px-5 py-2.5 font-medium hover:bg-[#AC5B40] disabled:opacity-60">{postingDaily ? "Posting…" : "Post encouragement"}</button>
          </div>
        )}
        {daily.map((p) => (
          <div key={p.id} className="relative rounded-2xl border border-[#D89A8C] bg-[#F9ECE8] p-6 fade-up" data-testid={`daily-post-${p.id}`}>
            {isAdmin && (
              <button onClick={() => removeDaily(p.id)} data-testid={`daily-remove-${p.id}`} title="Remove now"
                className="absolute top-4 right-4 p-1.5 rounded-full text-[#615A50] hover:text-[#C46B4E] hover:bg-white/60 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <div className="flex items-center gap-2 text-[#B76B61] pr-8">
              <Sparkles className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wide">Today's Encouragement</span>
              <span className="text-xs text-[#9A958C] font-normal">· {timeLeft(p.expires_at)}</span>
            </div>
            <p className="text-[#2A2621] mt-2 leading-relaxed whitespace-pre-wrap text-lg font-heading">{p.body}</p>
          </div>
        ))}
      </div>

      {visibleAnnouncements.length > 0 && (
        <div className="space-y-4 fade-up" data-testid="dash-announcements">
          {visibleAnnouncements.slice(0, 5).map((a) => (
            <div key={a.id} className="relative rounded-2xl border border-[#D5CEBF] bg-[#E7DFCE] p-6" data-testid={`dash-announcement-${a.id}`}>
              <button onClick={() => dismissAnnouncement(a.id)} data-testid={`dismiss-announcement-${a.id}`} title="Dismiss"
                className="absolute top-4 right-4 p-1.5 rounded-full text-[#615A50] hover:text-[#C46B4E] hover:bg-[#FAF7EE]/60 transition-colors">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 text-[#3D5844] pr-8">
                <Megaphone className="w-5 h-5" />
                <h3 className="font-heading text-2xl">{a.title}</h3>
              </div>
              <p className="text-[#615A50] mt-2 leading-relaxed whitespace-pre-wrap">{a.body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Current Word & Wellness */}
      <div className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-7 fade-up">
        <div className="flex items-center gap-2 text-[#3D5844]">
          <Youtube className="w-5 h-5" />
          <span className="text-sm font-semibold uppercase tracking-wide">Current Word & Wellness</span>
        </div>
        {latestVideo ? (
          <div className="mt-4 flex flex-col sm:flex-row gap-5">
            <Link to="/reading" data-testid="dash-latest-video" className="relative shrink-0 w-full sm:w-64 rounded-xl overflow-hidden group">
              {youtubeId(latestVideo.youtube_url) ? (
                <img src={`https://img.youtube.com/vi/${youtubeId(latestVideo.youtube_url)}/hqdefault.jpg`} alt={latestVideo.title} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-[#3D5844]/10" />
              )}
              <div className="absolute inset-0 bg-[#2A2621]/25 group-hover:bg-[#2A2621]/40 transition-colors flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#FAF7EE]/90 flex items-center justify-center"><Play className="w-5 h-5 text-[#C46B4E] ml-0.5" /></div>
              </div>
            </Link>
            <div>
              <h3 className="font-heading text-3xl text-[#2A2621]">{latestVideo.title}</h3>
              {latestVideo.description && <p className="text-[#615A50] mt-1 leading-relaxed line-clamp-3">{latestVideo.description}</p>}
              <Link to="/reading" data-testid="dash-continue-reading" className="inline-flex items-center gap-1 mt-4 text-[#C46B4E] font-medium hover:gap-2 transition-all">
                Watch now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : (
          <p className="text-[#615A50] mt-4">No teachings yet. Check back soon — a leader will share one.</p>
        )}
      </div>

      {/* Latest devotionals */}
      {latestDevos.length > 0 && (
        <div className="fade-up">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-3xl text-[#2A2621]">Today's Devotionals</h2>
            <Link to="/devotionals" className="text-sm text-[#C46B4E] font-medium">All devotionals</Link>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latestDevos.map((d) => (
              <Link key={d.id} to={`/devotionals/${d.id}`} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="flex items-center gap-2 text-[#3D5844] text-sm"><BookHeart className="w-4 h-4" /> {d.scripture_ref}</div>
                <h3 className="font-heading text-2xl text-[#2A2621] mt-1">{d.title}</h3>
                <p className="text-sm text-[#615A50] mt-1 line-clamp-2">{d.body}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 fade-up">
        <QuickLink to="/devotionals" icon={BookHeart} label="Devotionals" desc="Daily bread from the Word" />
        <QuickLink to="/reading" icon={BookOpen} label="Word & Wellness" desc="This week's teaching" />
        <QuickLink to="/prayer" icon={HandHeart} label="Prayer & Praise" desc="Lift one another up" />
        <QuickLink to="/messages" icon={MessageCircle} label="Message a Leader" desc="Reach a community leader" />
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} className="rounded-2xl border border-[#D5CEBF] bg-[#FAF7EE] p-6 hover:bg-[#F3EFE4] transition-colors flex items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-[#3D5844]/10 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#3D5844]" />
      </div>
      <div>
        <p className="font-medium text-[#2A2621]">{label}</p>
        <p className="text-sm text-[#615A50]">{desc}</p>
      </div>
    </Link>
  );
}
